import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const SubscriptionSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [message, setMessage] = useState('Processing your subscription...');

  useEffect(() => {
    console.log('✅ SUBSCRIPTION SUCCESS: Page loaded', {
      isAuthenticated,
      user: user?.username,
      hasToken: !!localStorage.getItem('authToken')
    });

    const sessionId = searchParams.get('session_id');

    if (!sessionId) {
      console.error('❌ SUBSCRIPTION SUCCESS: No session_id in URL');
      setStatus('error');
      setMessage('Invalid session. Please contact support.');
      return;
    }

    // Verify the subscription was successful
    const verifySubscription = async () => {
      try {
        console.log('✅ SUBSCRIPTION SUCCESS: Verifying session', { sessionId });

        const token = localStorage.getItem('authToken');
        console.log('✅ SUBSCRIPTION SUCCESS: Retrieved token from localStorage', {
          hasToken: !!token,
          tokenLength: token?.length
        });

        if (!token) {
          console.error('❌ SUBSCRIPTION SUCCESS: No auth token found in localStorage');
          setStatus('error');
          setMessage('Session expired. Please log in again.');
          setTimeout(() => navigate('/pricing'), 3000);
          return;
        }

        // Get updated subscription status from backend
        const response = await fetch('https://wandervibe.app/api/subscriptions/status', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        console.log('✅ SUBSCRIPTION SUCCESS: Status response', {
          status: response.status,
          ok: response.ok
        });

        if (!response.ok) {
          const errorData = await response.json();
          console.error('❌ SUBSCRIPTION SUCCESS: Failed to get subscription status', errorData);
          throw new Error(errorData.error || 'Failed to verify subscription');
        }

        const data = await response.json();
        console.log('✅ SUBSCRIPTION SUCCESS: Subscription verified', data);

        setStatus('success');
        setMessage('Your subscription is now active! Redirecting to your account...');

        // Redirect to account/subscription page after 2 seconds
        setTimeout(() => {
          console.log('✅ SUBSCRIPTION SUCCESS: Redirecting to /account/subscription');
          navigate('/account/subscription');
        }, 2000);

      } catch (error) {
        console.error('❌ SUBSCRIPTION SUCCESS: Error verifying subscription:', error);
        setStatus('error');
        setMessage('There was an issue verifying your subscription. Please check your account or contact support.');

        // Redirect to pricing page after 5 seconds on error
        setTimeout(() => {
          navigate('/pricing');
        }, 5000);
      }
    };

    verifySubscription();
  }, [searchParams, navigate, isAuthenticated, user]);

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f5f5f5',
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '48px',
        maxWidth: '500px',
        width: '100%',
        textAlign: 'center',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
      }}>
        {status === 'processing' && (
          <>
            <div style={{
              width: '64px',
              height: '64px',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #2196F3',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 24px'
            }}></div>
            <h1 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#1a1a1a',
              marginBottom: '12px'
            }}>
              Processing Subscription
            </h1>
            <p style={{
              fontSize: '16px',
              color: '#666',
              lineHeight: '1.5'
            }}>
              {message}
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div style={{
              width: '64px',
              height: '64px',
              backgroundColor: '#4CAF50',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px'
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path
                  d="M20 6L9 17L4 12"
                  stroke="white"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h1 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#1a1a1a',
              marginBottom: '12px'
            }}>
              Subscription Activated!
            </h1>
            <p style={{
              fontSize: '16px',
              color: '#666',
              lineHeight: '1.5'
            }}>
              {message}
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div style={{
              width: '64px',
              height: '64px',
              backgroundColor: '#f44336',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px'
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path
                  d="M18 6L6 18M6 6L18 18"
                  stroke="white"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h1 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#1a1a1a',
              marginBottom: '12px'
            }}>
              Verification Issue
            </h1>
            <p style={{
              fontSize: '16px',
              color: '#666',
              lineHeight: '1.5',
              marginBottom: '24px'
            }}>
              {message}
            </p>
            <button
              onClick={() => navigate('/pricing')}
              style={{
                backgroundColor: '#2196F3',
                color: 'white',
                padding: '12px 24px',
                borderRadius: '8px',
                border: 'none',
                fontSize: '16px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              Return to Pricing
            </button>
          </>
        )}

        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    </div>
  );
};

export default SubscriptionSuccess;
